/**
 * Which languages a template seals, and what it still owes each of them.
 *
 * The server REFUSES to publish a release that seals a language it cannot draw: the publish gate
 * calls `localeRenderContent` for every sealed language other than the declared one and returns
 * `secondary_locale_render_not_configured` naming the words that would otherwise have been drawn in
 * the wrong language. That gate is the authority and is not weakened here.
 *
 * This is the same reading, run in the editor while the author is still typing, so the message lands
 * on the field that is empty instead of arriving as one refusal after «Yayınla». It is a HINT, not a
 * gate — exactly as the LinkedIn organisation's field errors are — and the two are kept from drifting
 * by `tests/fixtures/sealed-locale-gap-vectors.json`, which both packages run.
 */

export const SEALED_LOCALES = ['tr', 'en'] as const;
export type SealedLocale = (typeof SEALED_LOCALES)[number];

export const isSealedLocale = (v: unknown): v is SealedLocale => v === 'tr' || v === 'en';

/** The other supported language. Total, with two locales. */
export const otherLocale = (l: SealedLocale): SealedLocale => (l === 'tr' ? 'en' : 'tr');

/** One place a sealed language has nothing of its own to draw. */
export interface SealedLocaleGap {
  locale: SealedLocale;
  /** `title`, `background`, `layer:<key>`, or `defaultLanguage`. The server's own names. */
  where: string;
  /** The words — or the asset — that would otherwise be drawn in the wrong language. */
  wouldDraw: string | null;
}

/** What the reading needs, named so a caller cannot pass the wrong half of a draft. */
export interface SealedLocaleInput {
  defaultLanguage: unknown;
  sealedLanguages: unknown;
  localizedMetadata: Record<string, Record<string, unknown>> | null | undefined;
  layers: readonly {
    id?: unknown;
    kind?: unknown;
    fieldKey?: unknown;
    text?: unknown;
    textLocalized?: unknown;
  }[];
  background: Record<string, unknown> | null | undefined;
  localizedBackground: Record<string, unknown> | null | undefined;
}

const asText = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t === '' ? null : t;
};

const asObject = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** The languages the draft says it seals, read the way the server reads them. */
export function sealedLanguagesOf(value: unknown): SealedLocale[] {
  if (!Array.isArray(value)) return [];
  const out: SealedLocale[] = [];
  for (const v of value) if (isSealedLocale(v) && !out.includes(v)) out.push(v);
  return out;
}

/** True when a background object actually names an asset with a digest, the way the server checks. */
export function namesAsset(value: unknown): string | null {
  const bg = asObject(value);
  const ref = asObject(bg.assetRef);
  const id = asText(ref.assetId) ?? asText(bg.assetId);
  const sha = asText(ref.sha256) ?? asText(bg.sha256);
  return id !== null && sha !== null ? id : null;
}

/**
 * Every gap, in the order the fields appear: the declaration, then per language the title, the
 * layers and the artwork. An empty list means the publish gate has nothing to refuse on this count.
 */
export function sealedLocaleGaps(input: SealedLocaleInput): SealedLocaleGap[] {
  const sealed = sealedLanguagesOf(input.sealedLanguages);
  if (sealed.length < 2) return [];

  const declared = isSealedLocale(input.defaultLanguage) ? input.defaultLanguage : null;
  const metadata = (locale: string): Record<string, unknown> =>
    asObject(asObject(input.localizedMetadata)[locale]);

  if (declared === null) {
    /* Both documents would take the same canonical words, so two «languages» would be one document
       under two names. The server refuses on the declaration itself; so does this. */
    const secondary = sealed.find((l) => l !== 'tr') ?? sealed[0] ?? 'en';
    return [{ locale: secondary as SealedLocale, where: 'defaultLanguage', wouldDraw: null }];
  }

  const canonicalTitle = asText(metadata(declared).title);
  const ownAsset = namesAsset(input.background);
  const gaps: SealedLocaleGap[] = [];

  /*
   * The title is owed only when something actually DRAWS it — the server's own condition
   * (`localeRenderContent`): a layer bound to the AUTO `credential_title` field. A template that
   * never draws the name on the artwork does not need it translated in order to be sealed.
   *
   * The English public PAGE is a separate question and a separate, non-blocking notice: it shows the
   * record's name in the language it was switched to, so an absent English title is a gap there even
   * when no layer draws it. `publicNameGaps` answers that one; this list is only what the publish
   * gate refuses, and the two are deliberately not merged.
   */
  const drawsTitle = input.layers.some((l) => l.kind !== 'static' && l.fieldKey === 'credential_title');

  for (const locale of sealed) {
    if (locale === declared) continue;
    if (drawsTitle && asText(metadata(locale).title) === null)
      gaps.push({ locale, where: 'title', wouldDraw: canonicalTitle });
    for (const layer of input.layers) {
      if (layer.kind !== 'static') continue;
      const literal = asText(layer.text);
      if (literal === null) continue; // an empty literal draws nothing in any language
      if (asText(asObject(layer.textLocalized)[locale]) === null)
        gaps.push({ locale, where: `layer:${String(layer.id ?? '')}`, wouldDraw: literal });
    }
    /* Required whenever the release has artwork at all: this side cannot see whether a picture
       carries words, so a language must say which picture it uses. Pointing it at the same asset is
       a choice the author makes; nothing is copied for them. */
    if (ownAsset !== null && namesAsset(asObject(input.localizedBackground)[locale]) === null)
      gaps.push({ locale, where: 'background', wouldDraw: `asset ${ownAsset}` });
  }
  return gaps;
}

/**
 * The sealed languages whose PUBLIC NAME is missing.
 *
 * Not a publish refusal and deliberately separate from `sealedLocaleGaps`: the public page shows the
 * record's name in the language it was switched to, and with no title for that language it shows
 * none. Reported so the author is told, rather than discovering it on a live page — and reported for
 * every sealed language, including the declared one, because a release with no name of its own in its
 * own language is a gap too.
 */
export function publicNameGaps(input: SealedLocaleInput): SealedLocale[] {
  const sealed = sealedLanguagesOf(input.sealedLanguages);
  if (sealed.length < 2) return [];
  return sealed.filter((l) => asText(asObject(asObject(input.localizedMetadata)[l]).title) === null);
}

/** The gaps for one language only — what a field's own warning needs. */
export const gapsFor = (gaps: readonly SealedLocaleGap[], locale: SealedLocale): SealedLocaleGap[] =>
  gaps.filter((g) => g.locale === locale);

/** The product's word for a language, for a message an author reads. */
export const localeLabel = (l: SealedLocale): string => (l === 'tr' ? 'Türkçe' : 'English');
